import * as React from "react";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Search,
  ChevronDown,
  Check,
  X,
  Loader2,
  Filter,
  RefreshCw,
  ArrowUpDown,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  useInfiniteFilterWorkflows,
} from "@/hooks/useLocalStorage";
import {
  parseWorkflowFilterResults,
} from "@/lib/api-services";
import type {
  WorkflowSortOption,
  WorkflowFilterItem,
} from "@/lib/api-services";
import { cn } from "@/lib/utils";

export type { WorkflowSortOption };

export interface FilterTypeSearchProps<T = any> {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  width?: string | number;
  className?: string;
  disabled?: boolean;
  initialSort?: WorkflowSortOption;
  // Backward compatibility props:
  filterTypeData?: T[];
  isLoading?: boolean;
  onSearch?: (query: string) => void;
}

const SORT_OPTIONS: { label: string; value: WorkflowSortOption; description: string }[] = [
  { label: "Standard", value: "Standard", description: "Default order" },
  { label: "Alphabetical", value: "Lex", description: "Sort alphabetically (A-Z)" },
  { label: "By IDs", value: "Id_based", description: "Sort by IDs" },
];

export function FilterTypeSearch<T = any>({
  value,
  onChange,
  placeholder = "Select Workflow Name",
  width,
  className,
  disabled = false,
  initialSort = "Standard",
  filterTypeData,
  isLoading: externalLoading,
  onSearch: externalOnSearch,
}: FilterTypeSearchProps<T>) {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [sortBy, setSortBy] = useState<WorkflowSortOption>(initialSort);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Debounce search input by 300ms
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchInput.trim());
      if (externalOnSearch) {
        externalOnSearch(searchInput.trim());
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput, externalOnSearch]);

  // If using self-querying (default when filterTypeData is not supplied or empty)
  const isSelfQueried = !filterTypeData;

  const {
    data,
    isLoading: isQueryLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    refetch,
  } = useInfiniteFilterWorkflows(
    debouncedQuery,
    sortBy,
    open && isSelfQueried
  );

  const isLoading = isSelfQueried ? isQueryLoading : !!externalLoading;

  // Flatten and normalize all items from infinite query or fallback to filterTypeData
  const allWorkflows: WorkflowFilterItem[] = useMemo(() => {
    if (!isSelfQueried && filterTypeData) {
      return filterTypeData.map((item: any, idx: number) => {
        const id = item?.PMId ?? item?.WFId ?? item?.Id ?? item?.id ?? idx;
        const name = item?.WorkflowName ?? item?.Name ?? item?.name ?? item?.title ?? "";
        return {
          id,
          name,
          code: item?.WFId ?? item?.Code ?? item?.code,
          type: item?.WorkflowType ?? item?.ProcessType ?? item?.type,
          status: item?.Status ?? item?.status,
          description: item?.Description ?? item?.description,
          raw: item,
        };
      });
    }

    if (!data?.pages) return [];

    const map = new Map<string | number, WorkflowFilterItem>();
    data.pages.forEach((page: any) => {
      const parsed = parseWorkflowFilterResults(page.data);
      parsed.items.forEach((item) => {
        if (!map.has(item.id)) {
          map.set(item.id, item);
        }
      });
    });

    return Array.from(map.values());
  }, [data, isSelfQueried, filterTypeData]);

  // Dual-detection scroll: IntersectionObserver on sentinel
  useEffect(() => {
    if (!open || !hasNextPage || isFetchingNextPage || !sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: listRef.current,
        rootMargin: "60px",
        threshold: 0.1,
      }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Fallback scroll handler
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const reachedBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 30;

      if (reachedBottom && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  // Focus input when popover opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setSearchInput("");
      setDebouncedQuery("");
    }
  }, [open]);

  // Handlers
  const handleSelectWorkflow = (item: WorkflowFilterItem) => {
    onChange(item.name);
    setOpen(false);
  };

  const handleSelectAll = () => {
    onChange("");
    setOpen(false);
  };

  const handleSelectCustomQuery = () => {
    if (debouncedQuery) {
      onChange(debouncedQuery);
      setOpen(false);
    }
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  // Check if typed query has exact match in current items
  const isExactMatchPresent = useMemo(() => {
    if (!debouncedQuery) return true;
    const lower = debouncedQuery.toLowerCase();
    return allWorkflows.some((w) => w.name.toLowerCase() === lower);
  }, [debouncedQuery, allWorkflows]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          style={width ? { width } : undefined}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-left transition-colors hover:bg-accent/40",
            className
          )}
        >
          <span
            className={cn(
              "truncate min-w-0 mr-2",
              value ? "font-medium text-foreground" : "text-muted-foreground"
            )}
          >
            {value || placeholder}
          </span>

          <div className="flex items-center space-x-1 shrink-0 ml-2">
            {value && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClearSelection}
                className="p-0.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Clear selection"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
                open && "transform rotate-180"
              )}
            />
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={4}
        style={{ width: "var(--radix-popover-trigger-width)" }}
        className="w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-trigger-width)] p-0 shadow-lg border border-border bg-popover rounded-md overflow-hidden z-50"
      >
        {/* Search Input Bar */}
        <div className="flex items-center px-3 py-2 border-b border-border bg-muted/30">
          <Search className="h-4 w-4 text-muted-foreground mr-2 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search workflow name..."
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground text-foreground"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              className="text-muted-foreground hover:text-foreground p-0.5"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {isLoading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-workfinity-500 ml-1.5 shrink-0" />
          )}
        </div>

        {/* Order By (Sort) Toolbar */}
        <div className="flex items-center justify-between px-3 py-1.5 bg-muted/40 border-b border-border/60 text-xs">
          <span className="flex items-center text-[11px] font-medium text-muted-foreground shrink-0 mr-2">
            <ArrowUpDown className="h-3 w-3 mr-1 text-muted-foreground/70" />
            Order by:
          </span>
          <div className="flex items-center space-x-1 overflow-x-auto py-0.5">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSortBy(opt.value)}
                title={opt.description}
                className={cn(
                  "px-2 py-0.5 rounded text-[11px] font-medium transition-all whitespace-nowrap",
                  sortBy === opt.value
                    ? "bg-workfinity-500 text-white shadow-xs font-semibold"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Action Options */}
        <div className="border-b border-border/60 bg-muted/10 p-1">
          {/* Option: All Workflows */}
          <button
            type="button"
            onClick={handleSelectAll}
            className={cn(
              "w-full flex items-center justify-between px-2.5 py-1.5 text-xs rounded-sm transition-colors text-left font-medium",
              !value
                ? "bg-workfinity-50 text-workfinity-700 font-semibold"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            <span>All workflows</span>
            {!value && <Check className="h-3.5 w-3.5 text-workfinity-600" />}
          </button>

          {/* Option: Filter by custom typed substring */}
          {debouncedQuery && !isExactMatchPresent && (
            <button
              type="button"
              onClick={handleSelectCustomQuery}
              className="w-full flex items-center px-2.5 py-1.5 text-xs rounded-sm transition-colors text-left text-workfinity-700 hover:bg-workfinity-50 font-medium"
            >
              <Filter className="h-3 w-3 mr-1.5 shrink-0" />
              <span className="truncate">
                Filter as &ldquo;<span className="underline">{debouncedQuery}</span>&rdquo;
              </span>
            </button>
          )}
        </div>

        {/* Scrollable Workflows List */}
        <div
          ref={listRef}
          onScroll={handleScroll}
          className="max-h-[260px] overflow-y-auto p-1 divide-y divide-border/20 text-sm"
        >
          {/* Loading Initial State */}
          {isLoading && allWorkflows.length === 0 && (
            <div className="py-6 text-center text-muted-foreground space-y-2">
              <Loader2 className="h-5 w-5 animate-spin mx-auto text-workfinity-500" />
              <p className="text-xs">Searching workflows...</p>
            </div>
          )}

          {/* Error State */}
          {isError && allWorkflows.length === 0 && (
            <div className="py-6 text-center text-red-500 space-y-2">
              <p className="text-xs">Failed to load workflows.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="h-7 text-xs"
              >
                <RefreshCw className="h-3 w-3 mr-1" /> Retry
              </Button>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !isError && allWorkflows.length === 0 && (
            <div className="py-6 text-center text-muted-foreground">
              <p className="text-xs">No workflows found matching &ldquo;{debouncedQuery}&rdquo;</p>
            </div>
          )}

          {/* Workflow Items */}
          {allWorkflows.map((item) => {
            const isSelected = value.toLowerCase() === item.name.toLowerCase();
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleSelectWorkflow(item)}
                className={cn(
                  "w-full flex items-center justify-between px-2.5 py-2 rounded-sm text-left transition-colors group",
                  isSelected
                    ? "bg-workfinity-50 text-workfinity-800 font-medium"
                    : "hover:bg-muted text-foreground"
                )}
              >
                <div className="flex flex-col min-w-0 mr-2">
                  <div className="flex items-center space-x-1.5">
                    <span className="truncate font-medium">{item.name}</span>
                    {item.code !== undefined && item.code !== "" && (
                      <span className="text-[10px] px-1.5 py-0.2 rounded bg-muted text-muted-foreground shrink-0 border border-border/40 font-mono">
                        ID: {item.code}
                      </span>
                    )}
                    {item.type && (
                      <span className="text-[10px] px-1.5 py-0.2 rounded bg-workfinity-50 text-workfinity-700 shrink-0 border border-workfinity-200">
                        {item.type}
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <span className="text-[11px] text-muted-foreground truncate mt-0.5">
                      {item.description}
                    </span>
                  )}
                </div>

                {isSelected && (
                  <Check className="h-4 w-4 text-workfinity-600 shrink-0 ml-1" />
                )}
              </button>
            );
          })}

          {/* Bottom Pagination Indicator */}
          {isFetchingNextPage && (
            <div className="py-2.5 flex items-center justify-center space-x-2 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-workfinity-500" />
              <span className="text-xs">Loading more workflows...</span>
            </div>
          )}

          {/* All items loaded indicator */}
          {!hasNextPage && allWorkflows.length >= 20 && !isLoading && (
            <div className="py-2 text-center text-[11px] text-muted-foreground/60 border-t border-border/30 mt-1">
              Showing all {allWorkflows.length} workflows
            </div>
          )}

          {/* Sentinel for IntersectionObserver */}
          <div ref={sentinelRef} className="h-1 w-full pointer-events-none" />
        </div>
      </PopoverContent>
    </Popover>
  );
}
